import {
  IncentiveCatalogReference,
  IncentiveItemEligibility,
  IncentiveItemScope,
  IncentiveItemSelectors,
} from 'types/incentive';

const referenceIdPatterns = {
  Product: /^gid:\/\/shopify\/Product\/\d+$/,
  Collection: /^gid:\/\/shopify\/Collection\/\d+$/,
};

export const emptyIncentiveItemSelectors = (): IncentiveItemSelectors => ({
  products: [],
  skus: [],
  collections: [],
  tags: [],
});

const normalizeLabels = (value: unknown, uppercase = false): string[] => {
  const labels = new Map<string, string>();
  if (!Array.isArray(value)) return [];
  value.forEach((rawValue) => {
    if (typeof rawValue !== 'string') return;
    const trimmed = rawValue.trim();
    const uppercaseLabel = uppercase ? trimmed.toUpperCase() : trimmed;
    const key = trimmed.toLowerCase();
    // Uppercasing must not change the backend's exact lowercase identity.
    const label =
      uppercaseLabel.toLowerCase() === key ? uppercaseLabel : trimmed;
    if (label && !labels.has(key)) labels.set(key, label);
  });
  return Array.from(labels.values());
};

const normalizeReferences = (
  value: unknown,
  resource: 'Product' | 'Collection',
): IncentiveCatalogReference[] => {
  if (!Array.isArray(value)) return [];
  const references = new Map<string, IncentiveCatalogReference>();
  const validId = referenceIdPatterns[resource];
  value.forEach((rawValue) => {
    if (!rawValue || typeof rawValue !== 'object') return;
    const id = typeof rawValue.id === 'string' ? rawValue.id.trim() : '';
    const title =
      typeof rawValue.title === 'string' ? rawValue.title.trim() : '';
    if (validId.test(id) && !references.has(id)) {
      references.set(id, { id, title: title || id });
    }
  });
  return Array.from(references.values());
};

const normalizeSelectors = (value: unknown): IncentiveItemSelectors => {
  const selectors =
    value && typeof value === 'object'
      ? (value as Partial<IncentiveItemSelectors>)
      : {};
  return {
    products: normalizeReferences(selectors.products, 'Product'),
    skus: normalizeLabels(selectors.skus, true),
    collections: normalizeReferences(selectors.collections, 'Collection'),
    tags: normalizeLabels(selectors.tags),
  };
};

const hasMalformedSelectors = (value: unknown): boolean => {
  if (value === undefined) return false;
  if (!value || typeof value !== 'object' || Array.isArray(value)) return true;
  const selectors = value as Record<string, unknown>;
  const selectorKeys = ['products', 'skus', 'collections', 'tags'];
  if (Object.keys(selectors).some((key) => !selectorKeys.includes(key)))
    return true;
  return selectorKeys.some((key) => {
    const entries = selectors[key];
    if (entries === undefined) return false;
    if (!Array.isArray(entries)) return true;
    const validId =
      key === 'products'
        ? referenceIdPatterns.Product
        : referenceIdPatterns.Collection;
    return entries.some((entry) => {
      if (key === 'skus' || key === 'tags')
        return typeof entry !== 'string' || !entry.trim();
      return (
        !entry ||
        typeof entry !== 'object' ||
        typeof entry.id !== 'string' ||
        !validId.test(entry.id.trim())
      );
    });
  });
};

export const hasIncentiveItemSelectors = (value: IncentiveItemSelectors) =>
  value.products.length +
    value.skus.length +
    value.collections.length +
    value.tags.length >
  0;

export const normalizeIncentiveItemEligibility = (
  value?: IncentiveItemEligibility | null,
): IncentiveItemEligibility => ({
  // Preserve an unsupported scope so validation blocks it instead of widening it.
  scope:
    hasMalformedSelectors(value?.include) ||
    hasMalformedSelectors(value?.exclude)
      ? ('INVALID_SELECTORS' as IncentiveItemScope)
      : value === undefined
      ? 'ALL_ITEMS'
      : ((value?.scope || '') as IncentiveItemScope),
  include: normalizeSelectors(value?.include),
  exclude: normalizeSelectors(value?.exclude),
});

export const getIncentiveItemEligibilityError = (
  value: IncentiveItemEligibility | undefined,
  ruleLabel: string,
): string | null => {
  const eligibility = normalizeIncentiveItemEligibility(value);
  if ((eligibility.scope as string) === 'INVALID_SELECTORS') {
    return `${ruleLabel} has invalid eligible-item selectors.`;
  }
  if (
    eligibility.scope !== 'ALL_ITEMS' &&
    eligibility.scope !== 'MATCHING_ITEMS'
  ) {
    return `${ruleLabel} has an unsupported eligible-items scope.`;
  }
  if (
    eligibility.scope === 'MATCHING_ITEMS' &&
    !hasIncentiveItemSelectors(eligibility.include)
  ) {
    return `${ruleLabel} needs at least one eligible product, SKU, collection, or product tag.`;
  }
  return null;
};

const describeSelectors = (selectors: IncentiveItemSelectors): string =>
  [
    selectors.products.length
      ? `${selectors.products.length} product${
          selectors.products.length === 1 ? '' : 's'
        }`
      : '',
    selectors.skus.length
      ? `${selectors.skus.length} SKU${selectors.skus.length === 1 ? '' : 's'}`
      : '',
    selectors.collections.length
      ? `${selectors.collections.length} collection${
          selectors.collections.length === 1 ? '' : 's'
        }`
      : '',
    selectors.tags.length
      ? `${selectors.tags.length} product tag${
          selectors.tags.length === 1 ? '' : 's'
        }`
      : '',
  ]
    .filter(Boolean)
    .join(', ');

export const summarizeIncentiveItemEligibility = (
  value?: IncentiveItemEligibility,
) => {
  const eligibility = normalizeIncentiveItemEligibility(value);
  const included =
    eligibility.scope === 'ALL_ITEMS'
      ? 'All items'
      : eligibility.scope === 'MATCHING_ITEMS'
      ? `Matching ${
          describeSelectors(eligibility.include) || 'items (none selected)'
        }`
      : 'Invalid item scope';
  return `${included}${
    hasIncentiveItemSelectors(eligibility.exclude)
      ? `; excluding ${describeSelectors(eligibility.exclude)}`
      : ''
  }`;
};
